import React, { useState } from 'react';

const DesignProcessUnique = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    const theme = {
        bg: '#F8F6F2',           // Warm Alabaster
        accent: '#D4AF37',       // Muted Brass
        textMain: '#2C302E',     // Deep Charcoal
        textMuted: '#6B7270',    // Slate Grey
        cardBg: '#FFFFFF',       // Pure White
        border: 'rgba(44, 48, 46, 0.12)'
    };

    const processSteps = [
        {
            number: '01',
            title: 'Briefing & Discovery',
            phase: 'Phase I',
            duration: '1 - 2 Weeks',
            description: 'Defining site parameters, functional requirements, and curated spatial aesthetics.',
            deliverables: [
                'In-depth lifestyle & spatial needs assessment',
                'Site analysis & architectural measurements',
                'Design aesthetic & material mood board curation',
                'Budget alignment & project timeline mapping'
            ]
        },
        {
            number: '02',
            title: 'Concept & Design',
            phase: 'Phase II',
            duration: '2 - 3 Weeks',
            description: 'Translating ideas into concrete 2D floor plans and high-definition 3D renders.',
            deliverables: [
                'Optimal space planning & spatial layout schemes',
                'Photorealistic 3D interior renders & lighting studies',
                'Custom joinery, furniture & cabinetry design',
                'Material, finish & fixture specifications'
            ]
        },
        {
            number: '03',
            title: 'Technical & BOQ',
            phase: 'Phase III',
            duration: '2 Weeks',
            description: 'Precision working drawings and meticulous material sourcing details.',
            deliverables: [
                'Detailed MEP, electrical, and ceiling plans',
                'Bill of Quantities (BOQ) and itemized costing',
                'Procurement of bespoke furniture & imported materials',
                'Vendor coordination & structural compliance review'
            ]
        },
        {
            number: '04',
            title: 'Execution & Build',
            phase: 'Phase IV',
            duration: '6 - 10 Weeks',
            description: 'On-site realization with strict quality control and craftsman supervision.',
            deliverables: [
                'On-site project management & daily quality checks',
                'Custom carpentry, masonry & ceiling installation',
                'Vendor timeline tracking & safety inspections',
                'Regular client updates with milestone progress reports'
            ]
        },
        {
            number: '05',
            title: 'Styling & Handover',
            phase: 'Phase V',
            duration: '1 Week',
            description: 'Final curated layers, white-glove setup, and turnkey space delivery.',
            deliverables: [
                'Deep cleaning & site snag list rectification',
                'Art placement, soft furnishings & decor styling',
                'Final walkthrough, warranties & key handover',
                'Post-occupancy maintenance guide and support'
            ]
        }
    ];

    const inlineAnimations = `
        @keyframes subtleScale {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .fade-card {
            animation: subtleScale 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @media (max-width: 991px) {
            .timeline-bar { overflow-x: auto !important; padding-bottom: 15px !important; }
            .content-grid { grid-template-columns: 1fr !important; }
            .section-wrapper { padding: 60px 20px !important; }
        }
    `;

    return (
        <section className="section-wrapper" style={{
            backgroundColor: theme.bg,
            color: theme.textMain,
            fontFamily: 'Georgia, serif',
            minHeight: '100vh',
            width: '100%',
            padding: '80px 40px',
            boxSizing: 'border-box',
            position: 'relative'
        }}>
            <style>{inlineAnimations}</style>

            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                
                {/* Header Section */}
                <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                    <span style={{
                        color: theme.accent,
                        fontSize: '0.85rem',
                        letterSpacing: '3px',
                        textTransform: 'uppercase',
                        fontWeight: '600',
                        display: 'block',
                        marginBottom: '10px'
                    }}>
                        Our Methodology
                    </span>
                    <h2 style={{
                        fontFamily: "'Cinzel', Georgia, serif", fontSize: '2rem', fontWeight: '300', margin: '15px 0 30px 0', letterSpacing: '1px', lineHeight: '1.1', color: theme.textMain, 
                    }}>
                        Interior Execution Process
                    </h2>
                     <div style={{
                    width: '60px',
                    height: '1px',
                    background: theme.accent,
                    margin: '24px auto 0 auto'
                }} />
                </div>

                {/* Horizontal Architectural Timeline Bar */}
                <div className="timeline-bar" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '50px',
                    position: 'relative',
                    borderBottom: `2px solid ${theme.border}`,
                    paddingBottom: '20px'
                }}>
                    {processSteps.map((step, idx) => {
                        const isActive = activeStep === idx;
                        const isHovered = hoveredIndex === idx;

                        return (
                            <div
                                key={idx}
                                onClick={() => setActiveStep(idx)}
                                onMouseEnter={() => setHoveredIndex(idx)}
                                onMouseLeave={() => setHoveredIndex(null)}
                                style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    padding: '0 10px'
                                }}
                            >
                                <span style={{
                                    display: 'block',
                                    fontSize: '0.8rem',
                                    fontFamily: 'monospace',
                                    color: isActive ? theme.accent : theme.textMuted,
                                    fontWeight: '600',
                                    marginBottom: '6px'
                                }}>
                                    {step.number}
                                </span>
                                <span style={{
                                    fontSize: '0.95rem',
                                    fontWeight: isActive ? '600' : '400',
                                    color: isActive ? theme.textMain : (isHovered ? theme.accent : theme.textMuted),
                                    transition: 'color 0.2s ease',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {step.title}
                                </span>

                                {/* Active Marker Line */}
                                {isActive && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '-22px',
                                        left: 0,
                                        width: '100%',
                                        height: '3px',
                                        backgroundColor: theme.accent,
                                        transition: 'all 0.3s ease'
                                    }} />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Main Dynamic Stage Display */}
                <div key={activeStep} className="fade-card content-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1.2fr',
                    gap: '40px',
                    backgroundColor: theme.cardBg,
                    borderRadius: '12px',
                    padding: '48px',
                    border: `1px solid ${theme.border}`,
                    boxShadow: '0 15px 35px rgba(0,0,0,0.04)'
                }}>
                    
                    {/* Left Column: Stage Overview & Meta */}
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                                <span style={{
                                    padding: '4px 12px',
                                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                                    color: theme.accent,
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    letterSpacing: '1px',
                                    borderRadius: '4px',
                                    textTransform: 'uppercase'
                                }}>
                                    {processSteps[activeStep].phase}
                                </span>
                                {/* <span style={{
                                    padding: '4px 12px',
                                    backgroundColor: 'rgba(44, 48, 46, 0.05)',
                                    color: theme.textMuted,
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    borderRadius: '4px'
                                }}>
                                    ⏱ {processSteps[activeStep].duration}
                                </span> */}
                            </div>

                            <h3 style={{
                                fontSize: '2.2rem',
                                fontWeight: '400',
                                margin: '0 0 16px 0',
                                color: theme.textMain
                            }}>
                                {processSteps[activeStep].title}
                            </h3>

                            <p style={{
                                fontSize: '1.05rem',
                                lineHeight: '1.6',
                                color: theme.textMuted,
                                margin: 0
                            }}>
                                {processSteps[activeStep].description}
                            </p>
                        </div>

                        {/* Navigation Arrows */}
                        <div style={{ display: 'flex', gap: '15px', marginTop: '40px' }}>
                            <button
                                onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))}
                                disabled={activeStep === 0}
                                style={{
                                    padding: '10px 24px',
                                    border: `1px solid ${theme.border}`,
                                    backgroundColor: 'transparent',
                                    color: theme.textMain,
                                    borderRadius: '20px',
                                    cursor: activeStep === 0 ? 'not-allowed' : 'pointer',
                                    opacity: activeStep === 0 ? 0.3 : 1,
                                    fontFamily: 'Georgia, serif',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                ← Previous Stage
                            </button>

                            <button
                                onClick={() => setActiveStep((prev) => Math.min(processSteps.length - 1, prev + 1))}
                                disabled={activeStep === processSteps.length - 1}
                                style={{
                                    padding: '10px 24px',
                                    border: 'none',
                                    backgroundColor: activeStep === processSteps.length - 1 ? theme.border : theme.textMain,
                                    color: '#FFFFFF',
                                    borderRadius: '20px',
                                    cursor: activeStep === processSteps.length - 1 ? 'not-allowed' : 'pointer',
                                    fontFamily: 'Georgia, serif',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Next Stage →
                            </button>
                        </div>
                    </div>

                    {/* Right Column: Deliverables Card List */}
                    <div style={{
                        backgroundColor: '#FAF9F5',
                        borderRadius: '8px',
                        padding: '32px',
                        border: `1px solid ${theme.border}`
                    }}>
                        <h4 style={{
                            margin: '0 0 20px 0',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            color: theme.accent
                        }}>
                            Key Stage Deliverables
                        </h4>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {processSteps[activeStep].deliverables.map((item, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: '#FFFFFF',
                                        padding: '14px 18px',
                                        borderRadius: '6px',
                                        border: `1px solid ${theme.border}`,
                                        fontSize: '0.95rem',
                                        color: theme.textMain
                                    }}
                                >
                                    <span style={{
                                        color: theme.accent,
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                        fontSize: '1rem'
                                    }}>
                                        ✦
                                    </span>
                                    {item}
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

            </div>
        </section>
    );
};

export default DesignProcessUnique;